import React, { memo, useMemo } from "react";
import { TableData } from "../tableContext/TableContext";
import TableRows from "../components/TableRows";
import {
  TableSectionActionsProps,
  TableSectionView,
} from "./sectionTable.interface";
import SectionTableTitle from "./SectionTableTitle";

const SectionRows = memo(function SectionRows({
  data,
  rowsById,
  ActionButtonsComponent,
}: {
  data: TableData[];
  rowsById: ReadonlyMap<TableData["id"], TableData>;
  ActionButtonsComponent?: any;
}) {
  const tableData = useMemo(
    () =>
      data.flatMap((row) => {
        const currentRow = rowsById.get(row.id);
        return currentRow ? [currentRow] : [];
      }),
    [data, rowsById],
  );

  return (
    <TableRows
      tableData={tableData}
      ActionButtonsComponent={ActionButtonsComponent}
    />
  );
});

function SectionTableBody({
  sections,
  rowsById,
  columnCount,
  showSectionTitles = true,
  ActionButtonsComponent,
  SectionActionsComponent,
}: {
  sections: TableSectionView[];
  rowsById: ReadonlyMap<TableData["id"], TableData>;
  columnCount: number;
  showSectionTitles?: boolean;
  ActionButtonsComponent?: any;
  SectionActionsComponent?: React.ComponentType<TableSectionActionsProps>;
}) {
  const renderRows = (data: TableData[]) => (
    <SectionRows
      data={data}
      rowsById={rowsById}
      ActionButtonsComponent={ActionButtonsComponent}
    />
  );

  const renderSection = (
    subsection: TableSectionView,
    depth: number,
  ): React.ReactNode => (
    <React.Fragment key={subsection.section.id}>
      <tr className="border-t border-brand/10 bg-brand/5">
        <th colSpan={columnCount} scope="rowgroup" className="py-3 text-left">
          <SectionTableTitle
            section={subsection.section}
            depth={depth}
            SectionActionsComponent={SectionActionsComponent}
          />
        </th>
      </tr>
      {renderRows(subsection.data)}
      {subsection.sections.map((child) => renderSection(child, depth + 1))}
    </React.Fragment>
  );

  return (
    <tbody>
      {sections.length > 0 && (
        <tr aria-hidden="true" className="!border-0">
          <td colSpan={columnCount} className="h-3 !p-0" />
        </tr>
      )}
      {sections.map((section) =>
        showSectionTitles ? (
          renderSection(section, 0)
        ) : (
          <React.Fragment key={section.section.id}>
            {renderRows(section.data)}
            {section.sections.map((subsection) => renderSection(subsection, 0))}
          </React.Fragment>
        ),
      )}
    </tbody>
  );
}

export default memo(SectionTableBody);
